// SAM-199 follow-up — front-desk "book a SERVICE" schedule + quantities step.
//
// Composes ServiceScheduleSlotPicker (which time, on the date HowLongControl
// already picked) + ServiceQuantitySelector (how many, derived from
// HowLongControl's Adults/Children — never a price) into a single selection,
// and reports it upward through ONE callback: onSelectionChange.
//
// SAM-199 SECOND PASS (design-access reorder): WHICH service is no longer
// chosen here — the owner's report was that the service picker rendered below
// the "How long?" step's own Next button, so confirming dates/guests skipped
// straight past it. `service` now comes in as a prop, chosen earlier in
// AccessForm (before "How long?", alongside Product/Service), and this
// component only handles the part that genuinely needs the date: time slot +
// quantities. The date and the guest count still come in as props (`date`,
// `adults`, `children`) from AccessForm, which reads them off the same
// HowLongControl mounted above.
//
// `selectedTime` is lifted into InvitationContext (selectedServiceTime), not
// local state — AccessForm unmounts/remounts on every view change (e.g. the
// operator taps "Dates" on the progress bar to go back), and a slot pick
// living in local state was silently lost on that remount while `duration`
// (already in context) survived. See InvitationContext.tsx's comment on
// `selectedService`/`selectedServiceTime` for the full rationale.

import { useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import type { Service } from '../../../hooks/useServices';
import { Notice } from '../../../components/ui/Notice';
import { ServiceScheduleSlotPicker } from './ServiceScheduleSlotPicker';
import { ServiceQuantitySelector } from './ServiceQuantitySelector';
import { ServiceAddOnsSelector } from './ServiceAddOnsSelector';
import { EventPanel, PreviewProblem, SeasonTag, UnitPanel } from './ServicePricingControls';
import {
  buildServiceBookingSelection, initialQuantities, initialAddOnQuantities, validateQuantities,
  type ServiceBookingSelection,
} from './serviceBooking';
import { deriveGuestMapping } from './serviceGuestMapping';
import {
  BLOCKING_PREVIEW_CODES, activeRateGroups, isPreviewError, effectiveGroupFor, hasGroupPrices, previewBookingPrice,
  priceUnitOf, requiresResourceKey,
} from './pricingPreview';
import type { BookingService } from './bookingService';
import { useBookingService } from './useBookingService';
import { useServiceSchedule, serviceDurationMinutes } from '../../../hooks/useServices';
import { useInvitationContext } from '../context/InvitationContext';
import { useT } from '../../../i18n/admin';

type Props = {
  service: Service;
  // From the shared HowLongControl (AccessForm reads invitation.duration /
  // invitation.guests.companions) — undefined/null until the operator has
  // confirmed a single day + guest count above.
  date: string | null; // "YYYY-MM-DD"
  adults: number;
  children: number;
  onSelectionChange: (selection: ServiceBookingSelection | null) => void;
};

// SAM-624 — the picked service is re-read from GET /services/description/{id} before anything is
// built from it (that endpoint strips the rate groups the viewer cannot buy and exposes the public
// pricing config); until it arrives no selection can be made.
export const ServiceBookingSelector = (props: Props) => {
  const { t } = useT();
  const { service: detail, isLoading, error } = useBookingService(props.service);
  const { onSelectionChange } = props;

  useEffect(() => {
    if (!detail) onSelectionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  if (error) {
    return <Notice variant="error">{t('booking.service.pricesError')}</Notice>;
  }
  if (isLoading || !detail) {
    return (
      <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Loader2 size={12} className="animate-spin" /> {t('booking.service.pricesLoading')}
      </div>
    );
  }
  return <ServiceBookingSelectorInner {...props} service={detail} />;
};

type InnerProps = Omit<Props, 'service'> & { service: BookingService };

const ServiceBookingSelectorInner = ({ service, date, adults, children, onSelectionChange }: InnerProps) => {
  const { invitation, updateInvitation } = useInvitationContext();
  const time = invitation?.selectedServiceTime ?? null;
  const setTime = (next: string | null) => updateInvitation({ selectedServiceTime: next });
  // Only used for the 'manual' fallback shape — see ServiceQuantitySelector.
  // Kept local (not lifted to context): it is re-derived from scratch (at each
  // option's minimum) whenever the service changes, so there is nothing
  // meaningful to preserve across a remount beyond what initialQuantities
  // already reconstructs.
  const manualQuantities = invitation?.manualServiceQuantities ?? {};
  const setManualQuantities = (next: Record<string, number>) => updateInvitation({ manualServiceQuantities: next });
  // SAM-590 — same remount-survival lift as manualQuantities, for add-on quantities.
  const addOnQuantities = invitation?.serviceAddOnQuantities ?? {};
  const setAddOnQuantities = (next: Record<string, number>) => updateInvitation({ serviceAddOnQuantities: next });
  // SAM-624 — rate-group picks and the cabana/unit pick (same lift).
  const pickedGroups = invitation?.serviceRateGroups ?? {};
  const resourceKey = invitation?.serviceResourceKey ?? null;

  const { data: schedule } = useServiceSchedule(service.id);

  // The date changed under an already-picked service — the old time slot may
  // no longer be valid for the new date. ServiceScheduleSlotPicker also clears
  // it on its own when the new date has no availability; this covers the plain
  // "picked a different valid date" case.
  useEffect(() => {
    setTime(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, service.id]);

  // The service itself changed (picked in AccessForm, above "How long?") —
  // the manual-fallback quantities belong to the PREVIOUS service's options
  // and must not carry over.
  useEffect(() => {
    setManualQuantities(initialQuantities(service.booking_options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id]);

  // SAM-590 — same rationale: the previous service's add-on quantities belong to options that no
  // longer exist on the new service.
  useEffect(() => {
    setAddOnQuantities(initialAddOnQuantities(service.settings?.add_ons));
    // SAM-624 — and so do its rate-group and cabana picks.
    updateInvitation({ serviceRateGroups: {}, serviceResourceKey: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id]);

  const options = service.booking_options || [];
  const addOns = service.settings?.add_ons || [];
  const mode = priceUnitOf(service);
  const groups = activeRateGroups(service);
  const adultsN = adults;
  const mapping = useMemo(
    () => deriveGuestMapping({ service, adults: adultsN, children }),
    [service, adultsN, children]
  );
  const quantities = mapping.kind === 'manual' ? manualQuantities : mapping.quantities;
  const quantitiesValid =
    mapping.kind === 'manual' ? validateQuantities(options, manualQuantities).valid : mapping.totalGuests > 0;

  // The group every requested line is priced in (the operator's pick, else the default rule).
  const resolvedGroups = useMemo(() => {
    const out: Record<string, string> = {};
    if (groups.length === 0) return out;
    options.forEach((opt) => {
      if ((quantities[opt.id] ?? 0) > 0 && hasGroupPrices(opt)) {
        const gid = effectiveGroupFor(opt, groups, pickedGroups[opt.id]);
        if (gid) out[opt.id] = gid;
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, JSON.stringify(quantities), JSON.stringify(pickedGroups)]);

  const preview = useMemo(
    () => previewBookingPrice({
      service, quantities, rateGroups: resolvedGroups, resourceKey, bookingDate: date, addOnQuantities,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, JSON.stringify(quantities), JSON.stringify(resolvedGroups), resourceKey, date, JSON.stringify(addOnQuantities)]
  );
  const blockedByPricing = isPreviewError(preview) && BLOCKING_PREVIEW_CODES.includes(preview.code);
  const complete = !!(date && time && quantitiesValid && !blockedByPricing);
  const quantitiesKey = JSON.stringify(quantities);
  const groupsKey = JSON.stringify(resolvedGroups);
  const addOnKey = JSON.stringify(addOnQuantities);

  useEffect(() => {
    if (!complete || !date || !time) {
      onSelectionChange(null);
      return;
    }
    onSelectionChange(
      buildServiceBookingSelection({
        service,
        date,
        time,
        quantities,
        addOnQuantities,
        rateGroups: resolvedGroups,
        // Only a unit-priced service with several resources needs (and gets) resource_key.
        resourceKey: requiresResourceKey(service) ? resourceKey : null,
        scheduleSource: (schedule as any)?.schedule_source,
        durationMinutes: serviceDurationMinutes(service),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, service, date, time, quantitiesKey, addOnKey, groupsKey, resourceKey, schedule]);

  const currency = options[0]?.currency || 'USD';

  return (
    <div>
      <ServiceScheduleSlotPicker service={service as Service} date={date} selectedTime={time} onSelectTime={setTime} />

      {mode === 'unit' && (
        <UnitPanel
          service={service}
          resourceKey={resourceKey}
          onResourceChange={(key) => updateInvitation({ serviceResourceKey: key })}
          preview={preview}
        />
      )}

      {mode === 'event' && <EventPanel preview={preview} pax={mapping.totalGuests} currency={currency} />}

      {options.length > 0 && (
        <div className="mt-3">
          <ServiceQuantitySelector
            options={options}
            mapping={mapping}
            manualQuantities={manualQuantities}
            onManualChange={setManualQuantities}
            mode={mode}
            preview={preview}
            rateGroups={groups}
            pickedGroups={pickedGroups}
            onRateGroupChange={(optionId, groupId) =>
              updateInvitation({ serviceRateGroups: { ...pickedGroups, [optionId]: groupId } })
            }
          />
        </div>
      )}

      <SeasonTag preview={preview} service={service} />
      <PreviewProblem preview={preview} />

      {/* SAM-590 — rendered after the guest quantities, per the ticket's ordering. */}
      <ServiceAddOnsSelector addOns={addOns} quantities={addOnQuantities} onChange={setAddOnQuantities} />
    </div>
  );
};
